import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useShowExUkContent } from '../../../../hooks/useShowExUkContent';
import { getEmailFirstRoutes } from '../../../../pages/email-first/emailFirstRouteNames';
import { EmailFirstForm } from '../emailFirstForm';
import {
    EMAIL_FIRST_PASSWORD_FORM_INPUT_NAME,
    EmailFirstPasswordFormInput,
} from '../inputs';
import { useEmailFirstForm } from '../useEmailFirstForm';
import { TriggerEvent } from '../../../../events';
import { useStateContext } from '../../../../modules/stateContext';
import { REGISTRATION_START_EVENT } from '../../../../../shared/data/customEvents/registration';
import renderQueryString from '../../../../../shared/urls/renderQueryString';
import { EditUserIdentifier } from '../../editUserIdentifier';
import { isExUk } from '../../../../utilities/isExUk';
import {
    StyledConsentAgeHeading,
    StyledTermsAndConditions,
} from './emailFirstPasswordForm.style';

const activeFields = [EMAIL_FIRST_PASSWORD_FORM_INPUT_NAME];

export const EmailFirstPasswordForm = () => {
    const userContext = useStateContext();

    const {
        isFederated,
        isSingleEntryAuth,
        location,
        suppressMarketing,
        featureToggles,
    } = userContext;

    const history = useHistory();

    const { emailFirstPaths } = getEmailFirstRoutes(
        isFederated,
        isSingleEntryAuth
    );

    const exUk = !location.isUk;

    const isSingleAuthExUkEnabled = isSingleEntryAuth && exUk;

    const submitToRegister =
        featureToggles.federatedExUk &&
        isFederated &&
        exUk &&
        suppressMarketing;

    const {
        fieldValues,
        handleBack,
        handleSubmit,
        handleSubmitInvalid,
    } = useEmailFirstForm({
        activeFields,
        countryCode: submitToRegister
            ? userContext.location.country
            : undefined,
        nextRoute: submitToRegister ? undefined : emailFirstPaths.details.path,
        preSubmit: undefined,
        prevRoute: emailFirstPaths.email.path,
        submitToRegister,
    });

    const showExUkContent = useShowExUkContent();

    const headingId = useMemo(() => {
        if (showExUkContent) {
            return 'emailFirst.passwordForm.titleExUk';
        }

        return 'emailFirst.passwordForm.title';
    }, [showExUkContent]);

    const onEditUserIdentifierEdit = useCallback(() => {
        history.push(
            `${emailFirstPaths.email.path}${renderQueryString.call({
                ...userContext,
            })}`
        );
    }, [history, userContext, emailFirstPaths]);

    const showAgeConsent = isSingleEntryAuth && isExUk(location);

    return (
        <EmailFirstForm
            activeFields={activeFields}
            heading={<FormattedMessage id={headingId} />}
            layoutContentContainerClassName={
                isSingleEntryAuth && 'u-margin-top'
            }
            onBackButtonClick={handleBack}
            onSubmit={handleSubmit}
            onSubmitInvalid={handleSubmitInvalid}
            submitButtonLabel={
                submitToRegister
                    ? 'register.button.submit.value'
                    : 'button.continue.value'
            }
        >
            {isSingleAuthExUkEnabled && (
                <EditUserIdentifier
                    userIdentifier={fieldValues?.username || ''}
                    onClick={onEditUserIdentifierEdit}
                    noBottomMargin={showAgeConsent}
                />
            )}
            {showAgeConsent && (
                <StyledConsentAgeHeading>
                    <FormattedMessage
                        id="emailFirst.emailForm.ageDeclarationConsentAge.title"
                        values={{
                            consentAge: 18,
                        }}
                    />
                </StyledConsentAgeHeading>
            )}
            <EmailFirstPasswordFormInput
                defaultValue={fieldValues && fieldValues.password}
                shouldValidate
            />
            {submitToRegister && (
                <StyledTermsAndConditions
                    isInternational={exUk}
                    linkTarget="_blank"
                    messageId="register.message.acceptTerms"
                />
            )}
        </EmailFirstForm>
    );
};

export function EmailFirstPasswordFormWithTriggerStartEvent() {
    return (
        <TriggerEvent
            component={EmailFirstPasswordForm}
            event={REGISTRATION_START_EVENT}
            unique
        />
    );
}
